import {
    bimData,
    costSchema,
    costStaticColumns,
    equipmentData,
    fieldLaborData,
    flatStaticColumns,
    forecastedBillingsData,
    forecastedBillingsStaticColumns,
    generalConditionsData,
    laborStaticColumns,
    materialData,
    otherCostsData,
    projectControlData,
    derivedProjectedData,
    projectedStaticColumns,
    shopLaborData,
    subcontractorData,
    targetBillingsData,
    targetBillingsStaticColumns,
    upsideDownsideAdditionalColumns,
    upsideDownsideAdditionalData,
    upsideDownsideColumns,
    upsideDownsideData,
    loadVarianceSummary,
} from './schedule-five-data';
import setupRefresh from './schedule-five-refresh';
import { syncAllGridScrolls, applyScrollPositionOnRefresh } from './schedule-five-scroll-sync';
import { captureWorkbookSheet } from '../../global/grid-export';

const monthColumnWidth = 135;

/**
 * A grid is read-only when the schedule five is marked ready for review, or
 * whenever an older month is on screen. Kept free of DOM access so the rule can
 * be covered directly.
 */
export const computeReadonly = (isHistorical, readyForReview) =>
    isHistorical === true || readyForReview === true;

/** True when the page is showing an older month rather than the current one. */
export const isHistoricalView = () => $('[name="IsHistorical"]').val() === 'true';

export const _readonly = function() {
    return computeReadonly(isHistoricalView(), $('.review-status-checkbox').prop('checked'));
};
const _month = $('[name="Month"]').val();
const _year = $('[name="Year"]').val();
const _startDate = `${_month}/1/${_year}`;

const applyDisabledStyles = (grid, idListToCheck) => {
    let idList = [];
    $.each(grid.dataSource.data(), (index, element) => {
        if (element.ProjectedHours === 0 || (idListToCheck != null && idListToCheck.includes(element.Id))) {
            idList.push(element.Id);
            const uid = element.uid;
            const rows = $(`[data-uid=${uid}]`);
            $.each(rows, (rowIndex, row) => {
                row.style.color = 'gray';
                row.style.background = 'lightgray';
            });
        }
    });

    return idList;
};

/* **************************************************************
 * Resource Entry object
 * **************************************************************/
export const _resourceEntryData = {
    'js-projected-grid': {
        columnWidth: monthColumnWidth,
        dataSource: derivedProjectedData,
        staticColumns: projectedStaticColumns,
        readonly: true,
        jsonField: 'Values',
        input: {
            type: 'columnBased',
            typeColumn: 'format',
        },
        dataBound: function(args) {
            $('.js-projected-grid .k-footer-template').hide();
        },
        startDate: _startDate,
    },
    'js-upside-downside': {
        columnWidth: monthColumnWidth,
        dataSource: upsideDownsideData,
        staticColumns: upsideDownsideColumns,
        readonly: _readonly,
        // Presentation only. The additional grid used to be read from here, which meant
        // it was re-read every time this one bound; it loads itself on construction and
        // is refreshed from the save paths in schedule-five-data.js instead.
        dataBound: function(args) {
            $('.js-upside-downside .k-footer-template').hide();
            $('.js-upside-downside .k-grid-content.k-auto-scrollable').css('overflow-x', 'hidden');
            $('.js-upside-downside .k-grid-content-locked')[0].style.removeProperty('height');
        },
        startDate: _startDate,
    },
    'js-upside-downside-additional': {
        columnWidth: monthColumnWidth,
        dataSource: upsideDownsideAdditionalData,
        staticColumns: upsideDownsideAdditionalColumns,
        readonly: true,
        dataBound: function(args) {
            $('.js-upside-downside-additional .k-grid-header').hide();
            $('.js-upside-downside-additional .k-footer-template').hide();
        },
        resize: function() {
            $('.js-upside-downside-additional .k-grid-header').hide();
            $('.js-upside-downside-additional .k-footer-template').hide();
        },
        startDate: _startDate,
    },
    'js-forecasted-billings-grid': {
        columnWidth: monthColumnWidth,
        dataSource: forecastedBillingsData,
        staticColumns: forecastedBillingsStaticColumns,
        readonly: _readonly,
        jsonField: 'Values',
        dataBound: function(args) {
            $('.js-forecasted-billings-grid .k-footer-template').hide();
            $('.js-forecasted-billings-grid .k-grid-content.k-auto-scrollable').css('overflow-x', 'hidden');
            $('.js-forecasted-billings-grid .k-grid-content-locked')[0].style.removeProperty('height');
        },
        startDate: _startDate,
    },
    'js-target-billings-grid': {
        columnWidth: monthColumnWidth,
        dataSource: targetBillingsData,
        staticColumns: targetBillingsStaticColumns,
        readonly: true,
        jsonField: 'Values',
        dataBound: function(args) {
            $('.js-target-billings-grid .k-grid-header').hide();
            $('.js-target-billings-grid .k-footer-template').hide();
        },
        input: {
            type: 'columnBased',
            typeColumn: 'format',
        },
        startDate: _startDate,
    },
    'js-equipment-grid': {
        columnWidth: monthColumnWidth,
        dataSource: equipmentData,
        staticColumns: flatStaticColumns,
        readonly: _readonly,
        startDate: _startDate,
    },
    'js-material-grid': {
        columnWidth: monthColumnWidth,
        dataSource: materialData,
        staticColumns: flatStaticColumns,
        readonly: _readonly,
        startDate: _startDate,
    },
    'js-project-control-grid': {
        columnWidth: monthColumnWidth,
        dataSource: projectControlData,
        staticColumns: laborStaticColumns,
        readonly: _readonly,
        input: { type: 'number' },
        resourceType: 'labor',
        startDate: _startDate,
        dataBound(data) {
            const costGrid = $(`.${data.dataSource.costForClassName}`).data('kendoResourceEntry');
            const idList = applyDisabledStyles(data);
            applyDisabledStyles(costGrid, idList);
        },
    },
    'js-bim-grid': {
        columnWidth: monthColumnWidth,
        dataSource: bimData,
        staticColumns: laborStaticColumns,
        readonly: _readonly,
        input: { type: 'number' },
        resourceType: 'labor',
        startDate: _startDate,
        dataBound(data) {
            const costGrid = $(`.${data.dataSource.costForClassName}`).data('kendoResourceEntry');
            const idList = applyDisabledStyles(data);
            applyDisabledStyles(costGrid, idList);
        },
    },
    'js-shop-labor-grid': {
        columnWidth: monthColumnWidth,
        dataSource: shopLaborData,
        staticColumns: laborStaticColumns,
        readonly: _readonly,
        input: { type: 'number' },
        resourceType: 'labor',
        startDate: _startDate,
        dataBound(data) {
            const costGrid = $(`.${data.dataSource.costForClassName}`).data('kendoResourceEntry');
            const idList = applyDisabledStyles(data);
            applyDisabledStyles(costGrid, idList);
        },
    },
    'js-field-labor-grid': {
        columnWidth: monthColumnWidth,
        dataSource: fieldLaborData,
        staticColumns: laborStaticColumns,
        input: { type: 'number' },
        readonly: _readonly,
        resourceType: 'labor',
        startDate: _startDate,
        dataBound(data) {
            const costGrid = $(`.${data.dataSource.costForClassName}`).data('kendoResourceEntry');
            const idList = applyDisabledStyles(data);
            applyDisabledStyles(costGrid, idList);
        },
    },
    'js-subcontractors-grid': {
        columnWidth: monthColumnWidth,
        dataSource: subcontractorData,
        staticColumns: flatStaticColumns,
        readonly: _readonly,
        startDate: _startDate,
    },
    'js-other-costs-grid': {
        columnWidth: monthColumnWidth,
        dataSource: otherCostsData,
        staticColumns: flatStaticColumns,
        readonly: _readonly,
        startDate: _startDate,
    },
    'js-general-condition-grid': {
        columnWidth: monthColumnWidth,
        dataSource: generalConditionsData,
        staticColumns: flatStaticColumns,
        readonly: _readonly,
        startDate: _startDate,
    },
};

/* **************************************************************
 * Element Setup
 * **************************************************************/
const setupGrids = () => {
    //Top Grid
    const projectedData = $('.js-projected-grid').kendoResourceEntry(_resourceEntryData['js-projected-grid']).data('kendoResourceEntry');
    const upsideDownside = $('.js-upside-downside').kendoResourceEntry(_resourceEntryData['js-upside-downside']).data('kendoResourceEntry');
    const additional = $('.js-upside-downside-additional').kendoResourceEntry(_resourceEntryData['js-upside-downside-additional']).data('kendoResourceEntry');
    const forecastedBillings = $('.js-forecasted-billings-grid').kendoResourceEntry(_resourceEntryData['js-forecasted-billings-grid']).data('kendoResourceEntry');
    const targetBillings = $('.js-target-billings-grid').kendoResourceEntry(_resourceEntryData['js-target-billings-grid']).data('kendoResourceEntry');

    syncAllGridScrolls();
};

const attachCollapsibleEvents = () => {
    const $collapsible = $('.collapsible__toggle--remote');
    if (!$collapsible.length) return; //Element doesn't exist

    $collapsible.on('collapsibleOpen', function(e) {
        const $grids = $(e.currentTarget).parent().find('[class$="-grid"],[data-role="resourceentry"]').not('.js-cost');
        if (!$grids.length) return; //Element doesn't exist

        $.each($grids, function(index, ele) {
            let $grid = $(ele);
            const $resource = $grid.data('kendoResourceEntry');
            if ($resource) { //Grid is already initialized
                $resource.grid.cancelRow();
                applyScrollPositionOnRefresh($resource);
                return;
            }
            initResourceGrid($grid);
        });
        syncAllGridScrolls();
    });

    initializeGrids();
    syncAllGridScrolls();
};

/**
 * Build the widget for one grid, or leave an already-built one alone.
 *
 * An already-built grid has already read: the resource-entry widget calls _loadGrid()
 * from its own init. Reloading it here fired a second request for every grid setupGrids
 * had just created, and because the first request was still in flight the dataSource had
 * no data yet, so fetch() went back to the server rather than reusing what was on its
 * way. Kendo stamps data-role="resourceentry" onto the element, which is how those grids
 * end up in this selector at all.
 */
export const initializeGrid = ($grid) => {
    const $resource = $grid.data('kendoResourceEntry');
    if ($resource) { //Grid is already initialized, and loaded with it
        $resource.grid.cancelRow();
        return $resource;
    }
    return initResourceGrid($grid);
};

const initializeGrids = () => {
    const $grids = $('[class$="-grid"],[data-role="resourceentry"]').not('.js-cost');
    if (!$grids.length) return; //Element doesn't exist

    $.each($grids, function(index, ele) {
        initializeGrid($(ele));
    });
};

const copyLaborDataItem = (dataItem) => {
    const rate = dataItem.LaborRate ?? 0;
    return {
        Id: dataItem.Id,
        ItemName: dataItem.ItemName,
        ProjectedCost: dataItem.ProjectedCost,
        ActualCost: dataItem.ActualCost,
        Difference: dataItem.DifferenceCost ?? 0,
        LaborRate: rate,
    };
};

// Every resource grid is initialised the same way. Labor sections additionally get
// their cost companion grid and warnings. Variance tags are not painted here: they
// come from one GetVarianceSummary call on load and from each save response, so a
// cost type whose section has never been opened still shows the right tag.
const initResourceGrid = ($element) => {
    const className = $element.attr('class');
    const $grid = $element.kendoResourceEntry(_resourceEntryData[className]).data('kendoResourceEntry');

    if ($grid.options.resourceType === 'labor') {
        $(`.${className}.js-cost`).data('cost-for', className);
        $grid.dataSource.costForClassName = className;
        $grid.dataSource.one('requestEnd', createCostGrid);
        $grid.grid.one('dataBound', addWarnings);
        return $grid;
    }

    return $grid;
};
const createCostGrid = function(e) {
    const me = this;
    const data = [];
    $.each(e.response, (itemIndex, item) => {
        const dataItem = copyLaborDataItem(item);
        const costValues = item.CostValues;
        if (costValues && Object.keys(costValues).length > 0) {
            dataItem.Values = costValues;
        } else {
            // Fallback: compute cost = hours × laborRate client-side
            const rate = item.LaborRate;
            dataItem.Values = Object.assign({}, item.Values);
            $.each(dataItem.Values, (valueIndex, value) => {
                dataItem.Values[valueIndex] = value * rate;
            });
        }
        data.push(dataItem);
    });
    const className = me.costForClassName;
    const config = {
        columnWidth: monthColumnWidth,
        dataSource: {
            data, schema: costSchema,
        }, staticColumns: costStaticColumns, readonly: true, startDate: _startDate,
    };
    let $grid = $(`.${className}.js-cost`);
    $grid = $grid.kendoResourceEntry(config).data('kendoResourceEntry');
    $(`.${className}:not(.js-cost)`).data('kendoResourceEntry').dataSource.bind('change', updateCostGrid);

    $grid.trigger('sync');

};
const updateCostGrid = function(e) {
    const me = this;
    const costGrid = $(`.${me.costForClassName}.js-cost`).data('kendoResourceEntry');
    const { dataSource } = costGrid;
    $.each(e.items, (itemIndex, item) => {
        const rate = item.LaborRate;
        const dataItem = dataSource.get(item.Id);
        $.each(costGrid.fieldInfo, (fieldIndex, fieldInfo) => {
            const name = fieldInfo.field;
            const value = item[name] * rate;
            dataItem[name] = value;
            dataItem.Values[name] = value;
        });
        dataItem.dirty = true;
    });
    costGrid.dataSource.sync();
};

const addWarnings = function(e) {
    const me = this;
    me.element.kendoTooltip({
        autoHide: true,
        showOn: 'mouseenter',
        filter: 'span.js-warning-tooltip',
        content: '<p>This cost code has zero projected hours entered.</p><p>Update the projected hours for this cost code in Viewpoint and then refresh the projected hours in Schedule 5.</p>',
        position: 'left',
        width: 200,
    });
};
const attachScrollPoints = () => {
    $('.scrollpoint--menu-aside').click(function() {
        const $ele = $(this);
        if ($ele.hasClass('open')) scrollToElement($ele);
    });
};
const attachAsideScrollPoints = () => {
    setTimeout(function() {
        const $ele = $('.menu-topics__toggle');
        $ele.click(function() {
            const target = $(this).data('collapsible-remote-target');
            const $scrollPoint = $(`#${target}`).parents('.scrollpoint--menu-aside');
            scrollToElement($scrollPoint);
        });
    }, 100);
};
const scrollToElement = ($ele) => {
    setTimeout(function() {
        $([document.documentElement, document.body]).animate({
            scrollTop: $ele.offset().top - 95,
        }, 1000);
    }, 250);
};
/* **************************************************************
 * Default function
 * **************************************************************/
export default function() {
    setupGrids();
    attachCollapsibleEvents();
    setupRefresh();

    // Paint all nine variance tags from the server. They render empty until this
    // returns, rather than showing a value the page has calculated for itself.
    loadVarianceSummary($('[name="Id"]').val());

//     Attach window events
    window.addEventListener('resize', function() {
        $('.js-upside-downside .k-grid-content-locked')[0].style.removeProperty('height');
    });
}

const getGrid = (selector) => $(selector).data('kendoResourceEntry')?.grid ?? null;

export const buildUpsideDownsideSheet = async () => {
    const mainGrid = getGrid('.js-upside-downside');
    const additionalGrid = getGrid('.js-upside-downside-additional');
    if (!mainGrid) return null;
    const mainSheet = await captureWorkbookSheet(mainGrid, 'Upside-Downside');
    if (!additionalGrid) return mainSheet;
    const additionalSheet = await captureWorkbookSheet(additionalGrid, 'additional');
    const dataRows = additionalSheet.rows.filter(r => r.type === 'data');
    for (const row of dataRows) {
        if (row.cells) row.cells.splice(1, 0, { value: '' }, { value: '' });
        mainSheet.rows.push(row);
    }
    return mainSheet;
};

export const getScheduleFiveGridEntries = () => [
    { grid: getGrid('.js-projected-grid'), sheetName: 'Forecasted Revenue' },
    { buildSheet: buildUpsideDownsideSheet },
    { grid: getGrid('.js-forecasted-billings-grid'), sheetName: 'Project Billing Forecast' },
    { grid: getGrid('.js-equipment-grid'), sheetName: 'Equipment' },
    { grid: getGrid('.js-material-grid'), sheetName: 'Material' },
    { grid: getGrid('.js-project-control-grid:not(.js-cost)'), sheetName: 'Project Control (Hours)' },
    { grid: getGrid('.js-project-control-grid.js-cost'), sheetName: 'Project Control (Cost)' },
    { grid: getGrid('.js-bim-grid:not(.js-cost)'), sheetName: 'BIM (Hours)' },
    { grid: getGrid('.js-bim-grid.js-cost'), sheetName: 'BIM (Cost)' },
    { grid: getGrid('.js-shop-labor-grid:not(.js-cost)'), sheetName: 'Shop Labor (Hours)' },
    { grid: getGrid('.js-shop-labor-grid.js-cost'), sheetName: 'Shop Labor (Cost)' },
    { grid: getGrid('.js-field-labor-grid:not(.js-cost)'), sheetName: 'Field Labor (Hours)' },
    { grid: getGrid('.js-field-labor-grid.js-cost'), sheetName: 'Field Labor (Cost)' },
    { grid: getGrid('.js-subcontractors-grid'), sheetName: 'Subcontractors' },
    { grid: getGrid('.js-other-costs-grid'), sheetName: 'Other Costs' },
    { grid: getGrid('.js-general-condition-grid'), sheetName: 'General Conditions' },
];
